/**
 * chat-director/cache — brief dedup of identical requests + freshness tracking.
 *
 * Cache identical generation requests briefly (cheap reuse for a flurry of the
 * same beats), but BIAS TOWARD FRESHNESS — dynamism is the point. A separate
 * "recently voiced" set stops the same beats being re-phrased into canned-feeling
 * duplicate spam.
 */
import type { Beat, VoiceRequest, VoiceResponse } from '@/shared';
import { normalizeLine } from './thread.js';

/** A stable key for a batched request: its beats + speakers + line count. */
export function requestKey(request: VoiceRequest): string {
  const beats = request.beats
    .map((b) => b.id)
    .slice()
    .sort()
    .join(',');
  const speakers = request.speakers
    .map((s) => s.trainerId)
    .slice()
    .sort()
    .join(',');
  return `${beats}|${speakers}|${request.desiredLineCount}`;
}

/** A stable key for a set of beats (what the director "already voiced"). */
export function beatsKey(beats: readonly Beat[]): string {
  return beats
    .map((b) => b.id)
    .slice()
    .sort()
    .join(',');
}

interface CacheEntry {
  readonly response: VoiceResponse;
  readonly at: number;
}

/**
 * A tiny TTL cache + recently-voiced ledger. `now` is injected (no wall-clock
 * dependency ⇒ deterministic in tests). Default TTL is short on purpose: reuse a
 * burst of identical requests, then go fresh again.
 */
export class ExchangeCache {
  private readonly ttlMs: number;
  private readonly entries = new Map<string, CacheEntry>();
  /** beatsKey → last time those beats were voiced (any source). */
  private readonly voicedAt = new Map<string, number>();
  /** normalized line text → last time that exact line was spoken (any speaker). */
  private readonly spokenAt = new Map<string, number>();

  constructor(ttlMs = 30_000) {
    this.ttlMs = ttlMs;
  }

  /**
   * True if this exact line (normalized, speaker-agnostic) was spoken within
   * `windowMs`. The director uses this to drop near-identical duplicate lines — so
   * two NPCs never emit the SAME canned line back-to-back, the bug D14 forbids.
   */
  wasRecentlySpoken(text: string, now: number, windowMs = 60_000): boolean {
    const at = this.spokenAt.get(normalizeLine(text));
    return at !== undefined && now - at <= windowMs;
  }

  /** Record that a line's text was just spoken (for near-identical dedup). */
  markSpoken(text: string, now: number): void {
    const norm = normalizeLine(text);
    if (norm.length > 0) this.spokenAt.set(norm, now);
  }

  /** Cached response for an identical request, if still within TTL. */
  get(request: VoiceRequest, now: number): VoiceResponse | null {
    const key = requestKey(request);
    const hit = this.entries.get(key);
    if (!hit) return null;
    if (now - hit.at > this.ttlMs) {
      this.entries.delete(key);
      return null;
    }
    return hit.response;
  }

  /** Store a response + mark its beats as freshly voiced. */
  put(request: VoiceRequest, response: VoiceResponse, now: number): void {
    this.entries.set(requestKey(request), { response, at: now });
    this.markVoiced(request.beats, now);
  }

  /** Record that a set of beats was just voiced (so we don't repeat it soon). */
  markVoiced(beats: readonly Beat[], now: number): void {
    for (const beat of beats) {
      this.voicedAt.set(beatsKey([beat]), now);
    }
  }

  /**
   * True if this exact beat was voiced within `windowMs`. The director uses this
   * to skip re-voicing the same canon back-to-back (no duplicate spam), while
   * still letting it resurface later for freshness over reuse.
   */
  wasRecentlyVoiced(beat: Beat, now: number, windowMs = 60_000): boolean {
    const at = this.voicedAt.get(beatsKey([beat]));
    return at !== undefined && now - at <= windowMs;
  }

  /** Drop expired TTL entries + stale voiced marks (keep the maps small). */
  prune(now: number, voicedWindowMs = 60_000): void {
    for (const [key, entry] of this.entries) {
      if (now - entry.at > this.ttlMs) this.entries.delete(key);
    }
    for (const [key, at] of this.voicedAt) {
      if (now - at > voicedWindowMs) this.voicedAt.delete(key);
    }
    for (const [key, at] of this.spokenAt) {
      if (now - at > voicedWindowMs) this.spokenAt.delete(key);
    }
  }

  /** Number of live cache entries (for tests / introspection). */
  get size(): number {
    return this.entries.size;
  }
}
